// baseService.ts
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { getEnvironmentVariable } from "../utils/env";
import { getMsalToken } from "../contexts/AzureEntraContext";

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const MAX_RETRY_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 5_000;

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getHeaderValue = (
  headers: unknown,
  name: string,
): string | number | undefined => {
  if (!headers) return undefined;
  const maybeAxiosHeaders = headers as { get?: (key: string) => unknown };
  const valueFromGetter = maybeAxiosHeaders.get?.(name);
  if (
    typeof valueFromGetter === "string" ||
    typeof valueFromGetter === "number"
  ) {
    return valueFromGetter;
  }

  const normalizedName = name.toLowerCase();
  const headerMap = headers as Record<string, string | number | undefined>;
  return headerMap[normalizedName] ?? headerMap[name];
};

const parseRetryAfterMs = (
  header: string | number | undefined,
): number | null => {
  if (header == null) return null;

  const value = String(header);
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(seconds * 1_000, RETRY_MAX_DELAY_MS);
  }

  const retryAt = Date.parse(value);
  if (!Number.isNaN(retryAt)) {
    return Math.min(Math.max(retryAt - Date.now(), 0), RETRY_MAX_DELAY_MS);
  }

  return null;
};

const getRetryDelayMs = (retryCount: number, error: AxiosError): number => {
  const retryAfterMs = parseRetryAfterMs(
    getHeaderValue(error.response?.headers, "retry-after"),
  );
  if (retryAfterMs != null) return retryAfterMs;

  const exponentialDelay = RETRY_BASE_DELAY_MS * 2 ** retryCount;
  const jitter = Math.floor(Math.random() * 100);
  return Math.min(exponentialDelay + jitter, RETRY_MAX_DELAY_MS);
};

const isRetryableError = (error: AxiosError): boolean => {
  if (error.code === "ERR_CANCELED") return false;

  const status = error.response?.status;
  if (status == null) return true;

  return RETRYABLE_STATUS_CODES.has(status);
};

const baseService = axios.create({
  baseURL: getEnvironmentVariable("REACT_APP_API_URL"),
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "Accept": "*/*",
    "Access-Control-Allow-Origin": "*"
  },
  withCredentials: false,
  proxy: false
});

baseService.interceptors.request.use(
  async (config) => {
    const token = await getMsalToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

baseService.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.error("Axios error:", error);
    const status = error.response?.status;

    // On auth failure, redirect to login immediately — do not retry.
    if (status === 401) {
      window.location.href = "/login";
      return Promise.reject(error);
    }

    const config = error.config as RetryableAxiosRequestConfig | undefined;
    if (!config || !isRetryableError(error)) {
      return Promise.reject(error);
    }

    config._retryCount = config._retryCount ?? 0;
    if (config._retryCount >= MAX_RETRY_ATTEMPTS) {
      return Promise.reject(error);
    }

    await sleep(getRetryDelayMs(config._retryCount, error));
    config._retryCount += 1;
    return baseService(config);
  }
);

export default baseService;
